/**
 * Compartir un CFDI — texto de WhatsApp, URLs de descarga y copiado al portapapeles.
 *
 * Vive aquí, y NO en un módulo importado por los dos bundles, a propósito:
 * app.js (facturadigital) y cfdi-crear.js son entries <script> clásicos, y un
 * módulo importado por ambos generaría un chunk compartido con `import` que
 * truena al cargarse sin type="module" (mismo gotcha que catalogos-sat y
 * swalErrorBox). Sólo app.js lo importa; el bundle cfdi-crear consume los
 * globales window.fd* en runtime, porque app.js se carga en TODAS las pantallas.
 *
 * El mensaje de WhatsApp es el MISMO que manda documentos/listado.php desde el
 * menú contextual del grid: si se bifurca, el cliente recibe dos textos distintos
 * según desde dónde se compartió. Los tests de Vitest lo fijan carácter a carácter.
 */

import { copiarTexto } from './portapapeles.js';

/** Base pública del sitio, tal como la arma hoy listado.php. */
export function baseUrlPublica() {
  return (typeof window !== 'undefined' && window.location)
    ? window.location.origin + '/'
    : '/';
}

export function urlPdfCfdi(uuid, base) {
  return (base || baseUrlPublica()) + 'docs/pdf/' + uuid;
}

export function urlXmlCfdi(rfcEmisor, uuid, base) {
  return (base || baseUrlPublica()) + 'docs/xml/' + rfcEmisor + '/' + uuid;
}

/**
 * Texto del mensaje de WhatsApp. Función pura: `base` se inyecta para poder
 * fijarlo en tests sin un DOM.
 *
 * @param {string} serfol      serie+folio ya concatenados (ej. "A123")
 * @param {string} fecha       fecha ya formateada DD-MM-YYYY
 * @param {number|string} montoTotal
 * @param {string} uuid
 * @param {string} rfcEmisor
 * @param {string} moneda
 * @param {string} [base]      base URL con diagonal final
 */
export function mensajeWhatsappCfdi(serfol, fecha, montoTotal, uuid, rfcEmisor, moneda, base) {
  var b = base || baseUrlPublica();
  var montoFormateado = parseFloat(montoTotal).toFixed(2);
  return 'Se anexa a continuación la factura electrónica CFDI con folio *' + serfol +
    '*, fecha de emisión del *' + fecha + '* y *monto total de $' + montoFormateado + ' ' + moneda +
    '*, así mismo los archivos PDF y XML podrán descargarse de los siguientes enlaces:' +
    '\n\nDescargar PDF: ' + urlPdfCfdi(uuid, b) +
    '\nDescargar XML: ' + urlXmlCfdi(rfcEmisor, uuid, b) +
    '\n\nAgradecemos su confianza y quedamos a sus ordenes,\nSaludos.' +
    '\n\n_Enviado desde www.facturadigital.com.mx_';
}

/**
 * URL de wa.me para un mensaje ya armado.
 *
 * Va con encodeURIComponent, no encodeURI: el mensaje es el VALOR del parámetro
 * `text`, y encodeURI deja pasar `&`, `#` y `+`. Un "&" legítimo en la serie o
 * en el RFC (rfcregex acepta \x26) partía el query string y WhatsApp recibía
 * el texto truncado, sin la liga del XML.
 */
export function urlWhatsapp(mensaje) {
  return 'https://wa.me/?text=' + encodeURIComponent(mensaje);
}

/**
 * Compartir un DOCUMENTO DEL SAT (Ola SAT PR 7, spec §5.4): mismo molde que el
 * CFDI de arriba, función pura y fijada carácter a carácter por vitest.
 *
 * `url` es la liga `d/<token>` que ya armó el servidor y `fechaDoc` viene
 * formateada ("15 de septiembre de 2026") o vacía. Fuera de la razón social del
 * emisor y de la liga, el texto NO lleva un solo dato del contribuyente: el
 * mensaje viaja por WhatsApp y se queda en el historial de quien lo recibe.
 *
 * La vigencia NO está incrustada en el texto (revisión de la Task 5, Minor #7):
 * el TTL real vive en `Satdoc_link_model::TTL_DIAS` y llega por `dias`. El
 * default de 7 es el mismo valor, y la sección L del arnés lo fija contra la
 * constante de PHP para que no puedan separarse en silencio.
 *
 * @param {string} tipo           'csf' | 'opinion' (cualquier otra cosa cae a Opinión)
 * @param {string} emisorNombre   razón social
 * @param {string} fechaDoc       fecha ya formateada, o ''
 * @param {string} url            liga d/<token>
 * @param {number} [dias]         vigencia real de la liga (default 7)
 */
export function mensajeWhatsappSatdoc(tipo, emisorNombre, fechaDoc, url, dias) {
  var nombre = tipo === 'csf' ? 'Constancia de Situación Fiscal' : 'Opinión del cumplimiento';
  var n = (dias === undefined || dias === null) ? 7 : Number(dias);
  var vigencia = (n === 1) ? '1 día' : n + ' días';
  return 'Hola, te comparto mi ' + nombre + (fechaDoc ? ' (' + fechaDoc + ')' : '') +
    ' de ' + emisorNombre + ': ' + url + ' — el enlace es válido ' + vigencia + '.';
}

/**
 * Deep link de wa.me con el mensaje del documento del SAT. Reusa urlWhatsapp(),
 * que desde #204 codifica con encodeURIComponent (antes encodeURI dejaba `&`
 * crudo y partía el parámetro `text`).
 */
export function urlWhatsappSatdoc(tipo, emisorNombre, fechaDoc, url, dias) {
  return urlWhatsapp(mensajeWhatsappSatdoc(tipo, emisorNombre, fechaDoc, url, dias));
}

/**
 * Registra los helpers globales que consumen las vistas y el bundle cfdi-crear.
 * Se llama una vez desde app.js.
 */
export function initCfdiShare(window) {
  /**
   * Abre WhatsApp Web/app con el CFDI ya redactado.
   * Firma heredada de documentos/listado.php: no cambiarla sin cambiar allá.
   */
  window.fdEnviarCfdiPorWhatsapp = function (serfol, fecha, montoTotal, uuid, rfcEmisor, moneda) {
    var mensaje = mensajeWhatsappCfdi(serfol, fecha, montoTotal, uuid, rfcEmisor, moneda);
    window.open(urlWhatsapp(mensaje), '_blank');
  };

  /**
   * Compartir documentos del SAT (Ola SAT PR 7). Se exponen las dos funciones
   * PURAS, no un "abrir WhatsApp": la pantalla de Documentos SAT pide la liga al
   * servidor por AJAX y sólo entonces arma el mensaje, así que necesita el texto
   * (para el botón de copiar) además del deep link. Las vistas los detectan con
   * `typeof window.fdUrlWhatsappSatdoc === 'function'` porque app.js y el bundle
   * de la pantalla no tienen orden garantizado.
   */
  window.fdMensajeWhatsappSatdoc = mensajeWhatsappSatdoc;
  window.fdUrlWhatsappSatdoc = urlWhatsappSatdoc;

  /**
   * Copia al portapapeles y avisa.
   *
   * @param {string} texto
   * @param {string} [etiqueta]   nombre de lo copiado ("UUID"). Sin etiqueta se
   *        conserva el aviso histórico de emitidos ("<texto> copiado al portapapeles").
   * @param {Element} [contenedor] elemento con el foco (modal o popup) del que
   *        colgar el textarea de respaldo. Sin él se usa <body>, que dentro de un
   *        modal falla por el focus trap.
   */
  window.fdCopiarAlPortapapeles = function (texto, etiqueta, contenedor) {
    return copiarTexto(texto, { contenedor: contenedor }).then(function (ok) {
      if (ok) {
        var aviso = etiqueta ? (etiqueta + ' copiado') : (texto + ' copiado al portapapeles');
        if (typeof window.swalSuccess === 'function') window.swalSuccess(aviso);
        return true;
      }
      // Nunca dejamos al usuario con un "no se pudo copiar" a secas: el navegador
      // bloqueó el portapapeles (permiso o contexto no seguro), así que le
      // enseñamos el valor para que lo seleccione a mano.
      var que = etiqueta || 'el texto';
      if (typeof window.swalErrorBox === 'function') {
        window.swalErrorBox(
          'Tu navegador no permitió copiar automáticamente. ' +
          'Selecciona ' + que + ' y cópialo con Ctrl+C (⌘+C):' +
          '<div class="mt-2 p-2 border rounded bg-light font-monospace small text-break user-select-all">' +
          String(texto == null ? '' : texto).replace(/[&<>"]/g, function (c) {
            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
          }) +
          '</div>',
          'Cópialo a mano'
        );
      }
      return false;
    });
  };
}
